import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  Plus, Search, Filter, Users, Shield, Wrench, 
  Briefcase, Eye, Edit, MoreVertical, Power,
  UserCheck, UserX, Calendar, Mail, Trash2
} from 'lucide-react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Modal from '../components/Modal'
import Pagination from '../components/ui/Pagination'
import { useToast } from '../components/Toast'

import usuariosService, { 
  UsuarioFilters,
  UsuarioFormData,
  formatarTipoUsuario,
  getTipoUsuarioColor,
  getTipoUsuarioIcon,
  formatarDataLogin,
  tiposUsuario
} from '../services/usuarios'
import { User } from '../types'
import { useAuth } from '../context/AuthContext'
import UsuarioForm from '../components/forms/UsuarioForm'
import UsuarioDetails from '../components/forms/UsuarioDetails'

const UsuariosPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { success, error } = useToast()
  const { user: currentUser } = useAuth()

  // Estados locais
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedUsuario, setSelectedUsuario] = useState<User | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Filtros
  const [filters, setFilters] = useState<UsuarioFilters>({
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '20'),
    search: searchParams.get('search') || '',
    tipo: searchParams.get('tipo') as any || undefined,
    sortBy: searchParams.get('sortBy') || 'nome',
    sortOrder: searchParams.get('sortOrder') as any || 'asc'
  })

  // Queries
  const { data: usuariosData, isLoading, refetch } = useQuery({
    queryKey: ['usuarios', filters],
    queryFn: () => usuariosService.list(filters)
  })

  // Mutations
  const createUsuarioMutation = useMutation({
    mutationFn: usuariosService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      setShowCreateModal(false)
      success('Utilizador criado com sucesso')
    },
    onError: (error: any) => {
      error(error.response?.data?.error || 'Erro ao criar utilizador')
    }
  })

  const updateUsuarioMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<UsuarioFormData> }) =>
      usuariosService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      setShowEditModal(false)
      success('Utilizador atualizado com sucesso')
    },
    onError: (error: any) => {
      error(error.response?.data?.error || 'Erro ao atualizar utilizador')
    }
  })

  const deleteUsuarioMutation = useMutation({
    mutationFn: usuariosService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      setShowDeleteModal(false)
      setSelectedUsuario(null)
      success('Utilizador excluído com sucesso')
    },
    onError: (error: any) => {
      error(error.response?.data?.error || 'Erro ao excluir utilizador')
    }
  })

  const toggleStatusMutation = useMutation({
    mutationFn: usuariosService.toggleStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      success(`Utilizador ${data.ativo ? 'ativado' : 'desativado'} com sucesso`)
    },
    onError: (error: any) => {
      error(error.response?.data?.error || 'Erro ao alterar status')
    }
  })

  // Handlers
  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }

  const handleFilterChange = (key: keyof UsuarioFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleCreate = () => {
    setSelectedUsuario(null)
    setShowCreateModal(true)
  }

  const handleEdit = (usuario: User) => {
    setSelectedUsuario(usuario)
    setShowEditModal(true)
  }

  const handleView = (usuario: User) => {
    setSelectedUsuario(usuario)
    setShowDetailsModal(true)
  }

  const handleCreateSubmit = async (data: UsuarioFormData) => {
    await createUsuarioMutation.mutateAsync(data)
  }

  const handleEditSubmit = async (data: UsuarioFormData) => {
    if (!selectedUsuario) return
    await updateUsuarioMutation.mutateAsync({ 
      id: selectedUsuario.id, 
      data 
    })
  }

  const handleDelete = (usuario: User) => {
    setSelectedUsuario(usuario)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!selectedUsuario) return
    deleteUsuarioMutation.mutate(selectedUsuario.id)
  }

  const handleToggleStatus = async (usuario: User) => {
    const acao = usuario.ativo ? 'desativar' : 'ativar'
    if (window.confirm(`Tem certeza que deseja ${acao} o utilizador "${usuario.nome}"?`)) {
      toggleStatusMutation.mutate(usuario.id)
    }
  }

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      search: '',
      tipo: undefined,
      sortBy: 'nome',
      sortOrder: 'asc'
    })
  }

  // Verificar se usuário atual é admin
  const isAdmin = currentUser?.tipo === 'ADMIN'

  return (
    <div className="space-y-6">
      {/* Header principal */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Utilizadores</h1>
          <p className="text-gray-600">
            {usuariosData?.pagination?.total || 0} utilizadores registados
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<Filter className="h-4 w-4" />}
            >
              Filtros
            </Button>
            <Button
              onClick={handleCreate}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Novo Utilizador
            </Button>
          </div>
        )}
      </div>

      {/* Barra de pesquisa */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Buscar por nome ou email..."
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Filtros avançados */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            label="Tipo de Utilizador"
            value={filters.tipo || ''}
            onChange={(e) => handleFilterChange('tipo', e.target.value || undefined)}
            options={[
              { value: '', label: 'Todos os tipos' },
              ...tiposUsuario.map(tipo => ({
                value: tipo.value,
                label: tipo.label
              }))
            ]}
          />
          
          <Select
            label="Ordenar por"
            value={filters.sortBy || 'nome'}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            options={[
              { value: 'nome', label: 'Nome' },
              { value: 'email', label: 'Email' },
              { value: 'tipo', label: 'Tipo' },
              { value: 'createdAt', label: 'Data Criação' },
              { value: 'ultimoLogin', label: 'Último Login' }
            ]}
          />

          <Select
            label="Ordem"
            value={filters.sortOrder || 'asc'}
            onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            options={[
              { value: 'asc', label: 'Crescente' },
              { value: 'desc', label: 'Decrescente' }
            ]}
          />

          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full"
            >
              Limpar Filtros
            </Button>
          </div>
        </div>
      )}

      {/* Lista de utilizadores */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Carregando utilizadores...</p>
          </div>
        ) : usuariosData?.usuarios?.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhum utilizador encontrado</h3>
            <p className="text-gray-600">
              {filters.search || filters.tipo
                ? 'Tente ajustar os filtros ou'
                : 'Comece por'
              } cadastrar um novo utilizador.
            </p>
            {isAdmin && (
              <Button
                className="mt-4"
                onClick={handleCreate}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Novo Utilizador
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilizador
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Último Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {usuariosData?.usuarios?.map((usuario) => {
                    const tipoConfig = getTipoUsuarioColor(usuario.tipo)
                    const tipoIcon = getTipoUsuarioIcon(usuario.tipo)
                    
                    return (
                      <tr key={usuario.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {usuario.nome.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {usuario.nome}
                                {usuario.id === currentUser?.id && (
                                  <span className="ml-2 text-xs text-blue-600">(Você)</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Mail className="h-3 w-3 mr-1" />
                                {usuario.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
                            tipoConfig === 'red' ? 'bg-red-100 text-red-800' :
                            tipoConfig === 'blue' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            <span className="mr-1">{tipoIcon}</span>
                            {formatarTipoUsuario(usuario.tipo)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatarDataLogin(usuario.ultimoLogin)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
                            usuario.ativo 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {usuario.ativo ? (
                              <>
                                <UserCheck className="h-3 w-3 mr-1" />
                                Ativo
                              </>
                            ) : (
                              <>
                                <UserX className="h-3 w-3 mr-1" />
                                Inativo
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(usuario)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {isAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(usuario)}
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleStatus(usuario)}
                                  title={usuario.ativo ? 'Desativar' : 'Ativar'}
                                  disabled={usuario.id === currentUser?.id}
                                >
                                  <Power className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(usuario)}
                                  title="Excluir"
                                  disabled={usuario.id === currentUser?.id}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {usuariosData && usuariosData.pagination && (
              <Pagination
                pagination={usuariosData.pagination}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Modal de criação */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Novo Utilizador"
          size="lg"
        >
          <div className="p-6">
            <UsuarioForm
              onSubmit={handleCreateSubmit}
              onCancel={() => setShowCreateModal(false)}
              isLoading={createUsuarioMutation.isPending}
            />
          </div>
        </Modal>
      )}

      {/* Modal de edição */}
      {showEditModal && selectedUsuario && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Editar Utilizador"
          size="lg"
        >
          <div className="p-6">
            <UsuarioForm
              usuario={selectedUsuario}
              onSubmit={handleEditSubmit}
              onCancel={() => setShowEditModal(false)}
              isLoading={updateUsuarioMutation.isPending}
            />
          </div>
        </Modal>
      )}

      {/* Modal de detalhes */}
      {showDetailsModal && selectedUsuario && (
        <Modal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title="Detalhes do Utilizador"
          size="lg"
        >
          <div className="p-6">
            <UsuarioDetails
              usuario={selectedUsuario}
              onEdit={isAdmin ? () => {
                setShowDetailsModal(false)
                setShowEditModal(true)
              } : undefined}
              onClose={() => setShowDetailsModal(false)}
              canEdit={isAdmin}
            />
          </div>
        </Modal>
      )}

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && selectedUsuario && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedUsuario(null)
          }}
          title="Confirmar Exclusão"
          size="sm"
        >
          <div className="p-6">
            <div className="mb-4">
              <p className="text-gray-700">
                Tem certeza que deseja excluir o utilizador <strong>{selectedUsuario.nome}</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Esta ação não pode ser desfeita. Todos os dados do utilizador serão permanentemente removidos.
              </p>
              {selectedUsuario.id === currentUser?.id && (
                <p className="text-sm text-red-600 mt-2">
                  ⚠️ Você não pode excluir seu próprio utilizador.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedUsuario(null)
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
                disabled={selectedUsuario.id === currentUser?.id || deleteUsuarioMutation.isPending}
                leftIcon={<Trash2 className="h-4 w-4" />}
              >
                {deleteUsuarioMutation.isPending ? 'Excluindo...' : 'Excluir Utilizador'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default UsuariosPage